import { useEffect, useRef } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Eraser,
  Italic,
  Link2,
  Underline,
  Unlink,
} from "lucide-react";
import { sanitizeHtml } from "./SafeHtmlContent";

const fontSizes = [{ label: "Small", value: "13px" }, { label: "Normal", value: "15px" }, { label: "Large", value: "19px" }, { label: "Extra large", value: "24px" }];
const lineSpacings = [{ label: "1", value: "1" }, { label: "1.5", value: "1.5" }, { label: "2", value: "2" }, { label: "2.5", value: "2.5" }];

function applyLineSpacing(value) {
  const selection = window.getSelection();
  if (!selection.anchorNode) return;
  let node = selection.anchorNode.nodeType === Node.TEXT_NODE ? selection.anchorNode.parentElement : selection.anchorNode;
  while (node && !node.isContentEditable) {
    if (["P", "DIV", "LI", "H1", "H2", "H3", "H4", "BLOCKQUOTE"].includes(node.tagName)) {
      node.style.lineHeight = value;
      return;
    }
    node = node.parentElement;
  }
}

export default function AdvancedRichTextEditor({
  value,
  onChange,
  testId = "advanced-rich-text-editor",
}) {
  const areaRef = useRef(null);
  useEffect(() => {
    document.execCommand("styleWithCSS", false, true);
  }, []);
  useEffect(() => {
    const sanitizedValue = sanitizeHtml(value);
    if (areaRef.current && areaRef.current.innerHTML !== sanitizedValue) {
      areaRef.current.innerHTML = sanitizedValue;
    }
  }, [value]);
  const emitSanitizedContent = () => onChange(sanitizeHtml(areaRef.current?.innerHTML || ""));
  const exec = (command, arg = null) => {
    areaRef.current?.focus();
    document.execCommand(command, false, arg);
    emitSanitizedContent();
  };
  const addLink = () => {
    const url = window.prompt("Enter URL (https://…)");
    if (url && /^https?:\/\//i.test(url)) exec("createLink", url);
    else if (url) window.alert("URL must start with http:// or https://");
  };

  return (
    <div className="rich-text-editor advanced" data-testid={testId}>
      <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
        <button type="button" onClick={() => exec("bold")} aria-label="Bold" data-testid={`${testId}-bold`}><Bold size={15} /></button>
        <button type="button" onClick={() => exec("italic")} aria-label="Italic" data-testid={`${testId}-italic`}><Italic size={15} /></button>
        <button type="button" onClick={() => exec("underline")} aria-label="Underline" data-testid={`${testId}-underline`}><Underline size={15} /></button>
        <label className="toolbar-color" title="Text color" data-testid={`${testId}-color`}>
          <span style={{ background: "#1e293b" }} />
          <input
            type="color"
            onChange={(event) => exec("foreColor", event.target.value)}
            aria-label="Text color"
          />
        </label>
        <select
          defaultValue=""
          onChange={(event) => {
            if (!event.target.value) return;
            const size = event.target.value;
            areaRef.current?.focus();
            document.execCommand("fontSize", false, "7");
            areaRef.current?.querySelectorAll("font[size='7']").forEach((element) => {
              element.removeAttribute("size");
              element.style.fontSize = size;
            });
            emitSanitizedContent();
          }}
          aria-label="Font size"
          data-testid={`${testId}-font-size`}
        >
          <option value="" disabled>Size</option>
          {fontSizes.map((size) => <option key={size.value} value={size.value}>{size.label}</option>)}
        </select>
        <select
          defaultValue=""
          onChange={(event) => {
            if (!event.target.value) return;
            areaRef.current?.focus();
            applyLineSpacing(event.target.value);
            emitSanitizedContent();
          }}
          aria-label="Line spacing"
          data-testid={`${testId}-line-spacing`}
        >
          <option value="" disabled>Spacing</option>
          {lineSpacings.map((spacing) => (
            <option key={spacing.value} value={spacing.value}>{spacing.label}</option>
          ))}
        </select>
        <button type="button" onClick={() => exec("justifyLeft")} aria-label="Align left" data-testid={`${testId}-align-left`}><AlignLeft size={15} /></button>
        <button type="button" onClick={() => exec("justifyCenter")} aria-label="Align center" data-testid={`${testId}-align-center`}><AlignCenter size={15} /></button>
        <button type="button" onClick={() => exec("justifyRight")} aria-label="Align right" data-testid={`${testId}-align-right`}><AlignRight size={15} /></button>
        <button type="button" onClick={addLink} aria-label="Insert link" data-testid={`${testId}-link`}><Link2 size={15} /></button>
        <button type="button" onClick={() => exec("unlink")} aria-label="Remove link" data-testid={`${testId}-unlink`}><Unlink size={15} /></button>
        <button type="button" onClick={() => exec("removeFormat")} aria-label="Clear formatting" data-testid={`${testId}-clear`}><Eraser size={15} /></button>
      </div>
      <div
        id={`${testId}-area`}
        ref={areaRef}
        className="rich-text-area"
        contentEditable
        suppressContentEditableWarning
        onInput={emitSanitizedContent}
        onBlur={emitSanitizedContent}
        data-testid={`${testId}-area`}
      />
    </div>
  );
}
